/**
 * 认证状态（React Context）。
 *
 * 启动顺序（见任务要求）：
 *   1. POST /auth/refresh —— 用 HttpOnly Cookie 静默换回 Access Token（存在内存里）
 *   2. GET  /auth/me      —— 拿到当前用户，才知道是家长还是学生、该进哪个界面
 * refresh 失败属于"未登录"，不是错误，直接进登录页。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { AccountRole } from '@fht/shared';
import { isSystemAdmin } from '@fht/shared';
import { setCurrentRole, subscribeSession } from './api/client';
import * as authApi from './api/auth';
import type { LoginPayload } from './api/auth';
import type { MeResponse } from './api/types';

export interface AuthContextValue {
  user: MeResponse | null;
  role: AccountRole | null;
  isLoading: boolean;
  isParent: boolean;
  isStudent: boolean;
  /** 系统管理员（见方案 2B）。注意它与 isParent 互斥：一个账号只有一个角色。 */
  isAdmin: boolean;
  /** 登录；失败时抛出 ApiError，由页面展示中文 message。 */
  login: (payload: LoginPayload) => Promise<MeResponse>;
  logout: () => Promise<void>;
  /** 重新拉取当前用户（改密、改昵称、绑定 TOTP 后用）。 */
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const bootstrapped = useRef(false);

  const applyUser = useCallback((next: MeResponse | null) => {
    setUser(next);
    setCurrentRole(next?.role ?? null);
  }, []);

  const reload = useCallback(async () => {
    try {
      const me = await authApi.me();
      applyUser(me);
    } catch {
      applyUser(null);
    }
  }, [applyUser]);

  // 启动引导：refresh → me
  useEffect(() => {
    // StrictMode 下 effect 会跑两次；refresh 令牌是轮换的，重复请求可能触发
    // 后端"令牌重放"判定并吊销全部会话，所以这里必须只跑一次。
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    let cancelled = false;
    void (async () => {
      try {
        const me = await authApi.restoreSession();
        if (!cancelled) applyUser(me);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyUser]);

  // 全局会话事件：refresh 救不回来时清空 user，路由守卫会把用户送到 /login
  useEffect(() => {
    return subscribeSession((event) => {
      if (event === 'session-expired' || event === 'signed-out') applyUser(null);
    });
  }, [applyUser]);

  const login = useCallback(
    async (payload: LoginPayload): Promise<MeResponse> => {
      const data = await authApi.login(payload);
      // 登录响应里只有精简用户信息，再取一次完整档案（email / relationLabel 等）
      let me: MeResponse = {
        id: data.user.id,
        role: data.user.role,
        username: data.user.username,
        nickname: data.user.nickname,
        email: null,
        relationLabel: null,
        mustChangePassword: data.user.mustChangePassword,
        totpEnabled: false,
      };
      try {
        me = await authApi.me();
      } catch {
        // /auth/me 失败不该让已经成功的登录白费，用登录响应里的精简信息兜底
      }
      applyUser(me);
      return me;
    },
    [applyUser],
  );

  const logout = useCallback(async () => {
    // authApi.logout 内部会 clearSession()，这里只负责把用户状态置空
    await authApi.logout();
    applyUser(null);
  }, [applyUser]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role: user?.role ?? null,
      isLoading,
      isParent: user?.role === 'parent',
      isStudent: user?.role === 'student',
      isAdmin: isSystemAdmin(user?.role),
      login,
      logout,
      reload,
    }),
    [user, isLoading, login, logout, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 必须在 <AuthProvider> 内使用');
  return ctx;
}
